// src/appbuilder/chat/chat-profilesync.js
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFirestore,
  collection,
  getDocs,
  query as fsQuery,
  where,
  limit as fsLimit,
} from "firebase/firestore";

/* ─────────────────────────────
   统一：Active Profile 的 key + event
   ───────────────────────────── */
export function makeActiveProfileKey(uid, appId) {
  return `lex_active_profile::${String(uid || "")}::${String(appId || "")}`;
}

export function broadcastActiveProfile({ uid, appId, profileId }) {
  try {
    window.dispatchEvent(
      new CustomEvent("lex:active-profile-changed", {
        detail: {
          uid: String(uid || ""),
          appId: String(appId || ""),
          profileId: String(profileId || ""),
        },
      })
    );
  } catch {}
}

/* ─────────────────────────────
   ✅ SIMPLE BRIDGE（关键）
   Chat 写：
     - lex_unified_scope::{viewerUid}
     - lex_unified_scope::last
   Login 读：
     - 先读 viewerUid key（若有）
     - 再读 last key（兜底）
   ───────────────────────────── */
export const LEX_UNIFIED_SCOPE_EVENT = "lex:unified-scope-changed";
export const LEX_UNIFIED_SCOPE_LAST_KEY = "lex_unified_scope::last";

function safeJsonParse(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readLS(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = safeJsonParse(raw);
    if (!obj || typeof obj !== "object") return null;
    return obj;
  } catch {
    return null;
  }
}

function writeLS(key, payload) {
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch {}
}

function emitScope(payload) {
  try {
    window.dispatchEvent(
      new CustomEvent(LEX_UNIFIED_SCOPE_EVENT, { detail: payload })
    );
  } catch {}
}

/* ─────────────────────────────
   统一：scope schema（Login/Chat 一致）
   - 传入非空 owner/app：写入缓存（viewer + last）
   - 传入为空：回读（viewer 优先，last 兜底）
   ───────────────────────────── */
export function buildUnifiedScope({
  embedded = false,
  effectiveOwnerUid = "",
  effectiveAppId = "",
  effectiveViewerUid = "",
} = {}) {
  const viewerUid = String(effectiveViewerUid || "");
  const ownerUidIn = String(effectiveOwnerUid || "");
  const appIdIn = String(effectiveAppId || "");

  const authed = !!viewerUid;
  const SCOPE_LS_KEY = viewerUid ? `lex_unified_scope::${viewerUid}` : "";

  const safeReadViewer = () => {
    if (!SCOPE_LS_KEY) return null;
    const obj = readLS(SCOPE_LS_KEY);
    if (!obj) return null;
    if (String(obj.viewerUid || "") !== viewerUid) return null;
    return {
      ownerUid: String(obj.ownerUid || ""),
      appId: String(obj.appId || ""),
      viewerUid: String(obj.viewerUid || ""),
    };
  };

  const safeReadLast = () => {
    const obj = readLS(LEX_UNIFIED_SCOPE_LAST_KEY);
    if (!obj) return null;
    return {
      ownerUid: String(obj.ownerUid || ""),
      appId: String(obj.appId || ""),
      viewerUid: String(obj.viewerUid || ""),
    };
  };

  const safeWrite = (ownerUid, appId) => {
    const payload = {
      viewerUid: String(viewerUid || ""),
      ownerUid: String(ownerUid || ""),
      appId: String(appId || ""),
      ts: Date.now(),
    };

    if (SCOPE_LS_KEY) writeLS(SCOPE_LS_KEY, payload);
    // ✅ 强兜底：无论 viewerUid 是否存在，都写 last
    writeLS(LEX_UNIFIED_SCOPE_LAST_KEY, payload);

    // 同 tab 立即同步
    emitScope(payload);
  };

  // ✅ Chat 场景：一旦传入非空，就写入缓存
  if (ownerUidIn && appIdIn) safeWrite(ownerUidIn, appIdIn);

  // ✅ Login 场景：如果传入为空，回读缓存
  let ownerUidOut = ownerUidIn;
  let appIdOut = appIdIn;

  // 先读 viewer key（更精准）
  if ((!ownerUidOut || !appIdOut) && authed) {
    const cached = safeReadViewer();
    if (cached) {
      if (!ownerUidOut) ownerUidOut = cached.ownerUid;
      if (!appIdOut) appIdOut = cached.appId;
    }
  }

  // 再读 last key（终极兜底）
  if (!ownerUidOut || !appIdOut) {
    const last = safeReadLast();
    if (last) {
      if (!ownerUidOut) ownerUidOut = last.ownerUid;
      if (!appIdOut) appIdOut = last.appId;
    }
  }

  return {
    authed,
    embedded: !!embedded,
    effectiveOwnerUid: String(ownerUidOut || ""),
    effectiveAppId: String(appIdOut || ""),
    effectiveViewerUid: String(viewerUid || ""),
  };
}

/* ─────────────────────────────
   统一：Active Profile Hook
   - 同 tab：custom event
   - 跨 tab：storage event
   ───────────────────────────── */
export function useUnifiedActiveProfile({ uid, appId }) {
  const LS_KEY = useMemo(() => makeActiveProfileKey(uid, appId), [uid, appId]);

  const readLSVal = useCallback(() => {
    try {
      return localStorage.getItem(LS_KEY) || "";
    } catch {
      return "";
    }
  }, [LS_KEY]);

  const writeLSVal = useCallback(
    (pid) => {
      const next = String(pid || "");
      try {
        if (next) localStorage.setItem(LS_KEY, next);
        else localStorage.removeItem(LS_KEY);
      } catch {}
      broadcastActiveProfile({ uid, appId, profileId: next });
    },
    [LS_KEY, uid, appId]
  );

  const [activeId, setActiveId] = useState(() => readLSVal());

  // uid/appId 变化时重新读
  useEffect(() => {
    if (!uid || !appId) return;
    setActiveId(readLSVal());
  }, [uid, appId, readLSVal]);

  // 同 tab + 跨 tab 同步
  useEffect(() => {
    if (!uid || !appId) return;

    const onEvent = (e) => {
      const d = e?.detail || {};
      if (String(d.uid) !== String(uid)) return;
      if (String(d.appId) !== String(appId)) return;
      setActiveId(String(d.profileId || ""));
    };

    const onStorage = (e) => {
      if (e.key !== LS_KEY) return;
      setActiveId(String(e.newValue || ""));
    };

    window.addEventListener("lex:active-profile-changed", onEvent);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("lex:active-profile-changed", onEvent);
      window.removeEventListener("storage", onStorage);
    };
  }, [uid, appId, LS_KEY]);

  const setActive = useCallback(
    (pid) => {
      const next = String(pid || "");
      setActiveId(next);
      if (uid && appId) writeLSVal(next);
    },
    [uid, appId, writeLSVal]
  );

  return { LS_KEY, activeId, setActive, readLS: readLSVal, writeLS: writeLSVal };
}

/* ─────────────────────────────
   统一：Profiles loader + debugInfo（Login/Chat 共用）
   Firestore:
   users/{ownerUid}/apps/{appId}/listings where authorUid == {viewerUid}
   ───────────────────────────── */
async function loadMyProfilesCore({ ownerUid, appId, viewerUid, limitN = 1000 }) {
  const db = getFirestore();
  const col = collection(
    db,
    "users",
    String(ownerUid),
    "apps",
    String(appId),
    "listings"
  );
  const q = fsQuery(
    col,
    where("authorUid", "==", String(viewerUid)),
    fsLimit(limitN)
  );
  const snap = await getDocs(q);

  return snap.docs.map((d) => {
    const root = d.data() || {};
    const inner =
      root &&
      typeof root === "object" &&
      root.data &&
      typeof root.data === "object"
        ? root.data
        : {};

    const title = String(
      inner.title || inner.name || root.title || root.name || "Profile"
    ).trim();

    const photo0 = Array.isArray(inner.photos) ? inner.photos[0] || "" : "";
    const avatarUrl = String(
      inner.avatarUrl ||
        inner.photoUrl ||
        inner.photoURL ||
        root.avatarUrl ||
        root.photoUrl ||
        root.photoURL ||
        photo0 ||
        ""
    ).trim();

    return { id: d.id, title, avatarUrl };
  });
}

/**
 * debugLoadProfilesUnified
 */
export async function debugLoadProfilesUnified({
  ownerUid,
  appId,
  viewerUid,
  fallbackOwnerUid = "",
  chatDebug = false,
  limitN = 1000,
} = {}) {
  const startedAt = Date.now();

  const info = {
    ownerUid: String(ownerUid || ""),
    appId: String(appId || ""),
    viewerUid: String(viewerUid || ""),
    fallbackOwnerUid: String(fallbackOwnerUid || ""),
    ok: false,
    count: 0,
    idsPreview: [],
    err: "",
    ms: 0,
    ts: new Date().toISOString(),
  };

  if (!info.ownerUid || !info.appId || !info.viewerUid) {
    info.err = `missing required: ownerUid/appId/viewerUid`;
    info.ms = Date.now() - startedAt;
    if (chatDebug) console.log("[profiles][debug] skip", info);
    return { rows: [], debugInfo: info };
  }

  try {
    if (chatDebug) {
      console.log("[profiles][debug] query primary", {
        path: `users/${info.ownerUid}/apps/${info.appId}/listings`,
        where: `authorUid == ${info.viewerUid}`,
      });
    }

    const rows = await loadMyProfilesCore({
      ownerUid: info.ownerUid,
      appId: info.appId,
      viewerUid: info.viewerUid,
      limitN,
    });

    info.ok = true;
    info.count = Array.isArray(rows) ? rows.length : 0;
    info.idsPreview = (rows || [])
      .slice(0, 8)
      .map((x) => x?.id)
      .filter(Boolean);

    if (
      info.count === 0 &&
      info.fallbackOwnerUid &&
      info.fallbackOwnerUid !== info.ownerUid
    ) {
      if (chatDebug) {
        console.log("[profiles][debug] query fallback", {
          path: `users/${info.fallbackOwnerUid}/apps/${info.appId}/listings`,
          where: `authorUid == ${info.viewerUid}`,
        });
      }

      const rows2 = await loadMyProfilesCore({
        ownerUid: info.fallbackOwnerUid,
        appId: info.appId,
        viewerUid: info.viewerUid,
        limitN,
      });

      const c2 = Array.isArray(rows2) ? rows2.length : 0;
      if (c2 > 0) {
        info.ownerUid = info.fallbackOwnerUid;
        info.count = c2;
        info.idsPreview = (rows2 || [])
          .slice(0, 8)
          .map((x) => x?.id)
          .filter(Boolean);
        info.ms = Date.now() - startedAt;
        return { rows: rows2, debugInfo: info };
      }
    }

    info.ms = Date.now() - startedAt;
    return { rows: rows || [], debugInfo: info };
  } catch (e) {
    info.err = String(e?.message || e || "Failed to load profiles");
    info.ms = Date.now() - startedAt;
    if (chatDebug) console.warn("[profiles][debug] ERROR", info);
    return { rows: [], debugInfo: info };
  }
}

/* ─────────────────────────────
   统一：融合 debug schema（两个页面一样）
   ───────────────────────────── */
export function buildUnifiedProfileDebug({
  authed = false,
  embedded = false,
  effectiveOwnerUid = "",
  effectiveAppId = "",
  effectiveViewerUid = "",

  activeProfileId = "",
  myProfilesLoading = false,
  myProfilesCount = 0,
  myProfilesError = "",
  myProfilesLastInfo = {},

  recentsCount = 0,
  recentsFilteredCount = 0,
  allUsersLoading = false,
  allUsersCount = 0,
  allUsersError = "",
  pickedPeerAppId = "",
  openRequestNonce = "",
  LS_ACTIVE_PROFILE_UNIFIED = "",

  listingPath = "",
  managePath = "",
} = {}) {
  return {
    authed: !!authed,
    embedded: !!embedded,
    effectiveOwnerUid: String(effectiveOwnerUid || ""),
    effectiveAppId: String(effectiveAppId || ""),
    effectiveViewerUid: String(effectiveViewerUid || ""),
    activeMyProfileId: String(activeProfileId || ""),
    myProfilesLoading: !!myProfilesLoading,
    myProfilesCount: Number(myProfilesCount || 0),
    myProfilesError: String(myProfilesError || ""),
    myProfilesLastInfo: myProfilesLastInfo || {},
    recentsCount: Number(recentsCount || 0),
    recentsFilteredCount: Number(recentsFilteredCount || 0),
    allUsersLoading: !!allUsersLoading,
    allUsersCount: Number(allUsersCount || 0),
    allUsersError: String(allUsersError || ""),
    pickedPeerAppId: String(pickedPeerAppId || ""),
    openRequestNonce: String(openRequestNonce || ""),
    LS_ACTIVE_PROFILE_UNIFIED: String(LS_ACTIVE_PROFILE_UNIFIED || ""),

    // Login-style也要存在
    uid: String(effectiveViewerUid || ""),
    appId: String(effectiveAppId || ""),
    activeProfileId: String(activeProfileId || ""),
    profiles: Number(myProfilesCount || 0),
    listingPath: String(listingPath || ""),
    managePath: String(managePath || ""),
  };
}